import React, { useState, useEffect } from 'react';
import { Card, Button, Badge, PageHeader, Modal, Input, Select, Alert } from '../../components/ui';
import api from '../../services/api';
import useAuthStore from '../../store/authStore';
import toast from 'react-hot-toast';

export default function EmailAccounts() {
  const { user } = useAuthStore();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [oauthLoading, setOauthLoading] = useState('');
  const [form, setForm] = useState({ name:'', email:'', type:'smtp', smtpHost:'', smtpPort:587, smtpUser:'', smtpPassword:'', imapHost:'', imapPort:993 });

  useEffect(() => {
    fetchAccounts();
    // Handle OAuth redirect success/error
    const params = new URLSearchParams(window.location.search);
    if (params.get('success')) { toast.success(`✅ ${params.get('success').replace('_',' ')} successfully!`); window.history.replaceState({}, '', '/client/email-accounts'); }
    if (params.get('error')) { toast.error(`OAuth failed: ${params.get('error').replace('_',' ')}`); window.history.replaceState({}, '', '/client/email-accounts'); }
  }, []);

  const fetchAccounts = async () => {
    try { const r = await api.get('/email-accounts'); setAccounts(r.data.data || []); }
    catch {} finally { setLoading(false); }
  };

  const connectOAuth = async (provider) => {
    setOauthLoading(provider);
    try {
      const r = await api.get(`/email-accounts/oauth/${provider}/url`);
      window.location.href = r.data.url;
    } catch (e) {
      const msg = e.response?.data?.message || `${provider} OAuth not configured`;
      toast.error(msg, { duration: 5000 });
    } finally { setOauthLoading(''); }
  };

  const addSMTP = async () => {
    if (!form.email || !form.smtpHost) return toast.error('Email and SMTP host required');
    const limit = user?.accountsLimit;
    if (limit !== -1 && accounts.length >= limit) return toast.error(`Account limit reached (${limit}). Upgrade to add more.`);
    setSaving(true);
    try { await api.post('/email-accounts', form); toast.success('Account connected!'); setShowModal(false); fetchAccounts(); }
    catch (e) { toast.error(e.response?.data?.message || 'Failed'); } finally { setSaving(false); }
  };

  const toggleWarmup = async (id, enabled) => {
    try { await api.put(`/email-accounts/${id}`, { warmupEnabled: !enabled }); fetchAccounts(); toast.success(`Warmup ${!enabled ? 'started' : 'stopped'}`); }
    catch { toast.error('Failed'); }
  };

  const deleteAccount = async (id) => {
    if (!window.confirm('Remove this account?')) return;
    try { await api.delete(`/email-accounts/${id}`); fetchAccounts(); toast.success('Removed'); } catch { toast.error('Failed'); }
  };

  const f = (k,v) => setForm(p=>({...p,[k]:v}));
  const limit = user?.accountsLimit === -1 ? '∞' : user?.accountsLimit;
  const atLimit = user?.accountsLimit !== -1 && accounts.length >= user?.accountsLimit;

  return (
    <div>
      <PageHeader title="Email Accounts" subtitle={`${accounts.length} / ${limit} accounts connected`} action={
        <Button onClick={() => setShowModal(true)} disabled={atLimit}>+ Connect Account</Button>
      } />

      {atLimit && <Alert type="warning" style={{ marginBottom:20 }}>Account limit reached ({user?.accountsLimit}). Upgrade your plan to connect more.</Alert>}

      {/* OAuth connect buttons */}
      <Card style={{ marginBottom:20 }}>
        <div style={{ fontWeight:700, marginBottom:14, fontSize:14 }}>🔗 Quick Connect</div>
        <div style={{ display:'flex', gap:12, flexWrap:'wrap' }}>
          <button onClick={() => connectOAuth('google')} disabled={atLimit || oauthLoading==='google'}
            style={{ display:'flex', alignItems:'center', gap:10, padding:'12px 24px', background:'#1a1a1a', border:'1px solid rgba(46,204,113,0.2)', borderRadius:12, color:'#fff', cursor:atLimit?'not-allowed':'pointer', opacity:atLimit?0.5:1, fontSize:14, fontWeight:600, transition:'all 0.2s' }}
            onMouseEnter={e => { if(!atLimit) e.currentTarget.style.borderColor='rgba(46,204,113,0.5)'; }}
            onMouseLeave={e => e.currentTarget.style.borderColor='rgba(46,204,113,0.2)'}>
            <svg width="20" height="20" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
            {oauthLoading==='google' ? 'Connecting...' : 'Connect Gmail / Google Workspace'}
          </button>
          <button onClick={() => connectOAuth('microsoft')} disabled={atLimit || oauthLoading==='microsoft'}
            style={{ display:'flex', alignItems:'center', gap:10, padding:'12px 24px', background:'#1a1a1a', border:'1px solid rgba(46,204,113,0.2)', borderRadius:12, color:'#fff', cursor:atLimit?'not-allowed':'pointer', opacity:atLimit?0.5:1, fontSize:14, fontWeight:600, transition:'all 0.2s' }}
            onMouseEnter={e => { if(!atLimit) e.currentTarget.style.borderColor='rgba(46,204,113,0.5)'; }}
            onMouseLeave={e => e.currentTarget.style.borderColor='rgba(46,204,113,0.2)'}>
            <svg width="20" height="20" viewBox="0 0 24 24"><path fill="#f25022" d="M1 1h10v10H1z"/><path fill="#00a4ef" d="M13 1h10v10H13z"/><path fill="#7fba00" d="M1 13h10v10H1z"/><path fill="#ffb900" d="M13 13h10v10H13z"/></svg>
            {oauthLoading==='microsoft' ? 'Connecting...' : 'Connect Microsoft / Outlook'}
          </button>
          <button onClick={() => setShowModal(true)} disabled={atLimit}
            style={{ display:'flex', alignItems:'center', gap:8, padding:'12px 24px', background:'transparent', border:'1px solid rgba(46,204,113,0.2)', borderRadius:12, color:'#888', cursor:atLimit?'not-allowed':'pointer', opacity:atLimit?0.5:1, fontSize:14, transition:'all 0.2s' }}
            onMouseEnter={e => { if(!atLimit) e.currentTarget.style.borderColor='rgba(46,204,113,0.5)'; }}
            onMouseLeave={e => e.currentTarget.style.borderColor='rgba(46,204,113,0.2)'}>
            ⚙️ Manual SMTP Setup
          </button>
        </div>
        <div style={{ marginTop:16, background:'rgba(46,204,113,0.04)', border:'1px solid rgba(46,204,113,0.12)', borderRadius:10, padding:'14px 16px' }}>
          <div style={{ fontSize:12, fontWeight:700, color:'#2ECC71', marginBottom:10 }}>🏢 Google Workspace Admin? Approve access first:</div>
          <ol style={{ margin:0, paddingLeft:18, fontSize:12, color:'#888', lineHeight:2 }}>
            <li>Go to your <a href="https://admin.google.com/ac/owl/list?tab=configuredApps" target="_blank" rel="noreferrer" style={{ color:'#2ECC71' }}>Google Workspace Admin Panel → Security → API Controls</a></li>
            <li>Click <strong style={{ color:'#ccc' }}>Configure new app</strong></li>
            <li>Search by Client ID: <code style={{ background:'#1a1a1a', padding:'2px 8px', borderRadius:5, color:'#2ECC71', fontSize:11, userSelect:'all', cursor:'text' }}>46311301170-6qgkgg8ohcvj7kdu042bc5jn132t70i8.apps.googleusercontent.com</code></li>
            <li>Select <strong style={{ color:'#ccc' }}>Mark MS Discoveries</strong> from results and click <strong style={{ color:'#ccc' }}>Select</strong></li>
            <li>Set access to <strong style={{ color:'#ccc' }}>Trusted</strong> and save — then click Connect Gmail above</li>
          </ol>
        </div>
      </Card>

      {loading ? <div style={{ color:'#555', textAlign:'center', padding:40 }}>Loading...</div> : (
        accounts.length === 0 ? (
          <Card style={{ textAlign:'center', padding:60 }}>
            <div style={{ fontSize:48, marginBottom:16 }}>✉️</div>
            <h3 style={{ color:'#ccc', marginBottom:8 }}>No accounts connected</h3>
            <p style={{ color:'#555', fontSize:14 }}>Connect Gmail, Google Workspace, Microsoft Outlook, or any SMTP account.</p>
          </Card>
        ) : (
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(300px,1fr))', gap:16 }}>
            {accounts.map(a => (
              <Card key={a.id}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:12 }}>
                  <div>
                    <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4 }}>
                      {a.type==='gmail' && <svg width="16" height="16" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>}
                      {a.type==='microsoft' && <svg width="16" height="16" viewBox="0 0 24 24"><path fill="#f25022" d="M1 1h10v10H1z"/><path fill="#00a4ef" d="M13 1h10v10H13z"/><path fill="#7fba00" d="M1 13h10v10H1z"/><path fill="#ffb900" d="M13 13h10v10H13z"/></svg>}
                      {a.type==='smtp' && <span style={{ fontSize:14 }}>⚙️</span>}
                      <span style={{ fontWeight:700, fontSize:13 }}>{a.name || a.email}</span>
                    </div>
                    <div style={{ fontSize:11, color:'#555' }}>{a.email}</div>
                  </div>
                  <Badge color={a.status==='active'?'success':a.status==='warming'?'info':a.status==='error'?'danger':'gray'}>{a.status}</Badge>
                </div>
                <div style={{ marginBottom:14 }}>
                  <div style={{ display:'flex', justifyContent:'space-between', fontSize:12, color:'#555', marginBottom:4 }}>
                    <span>🌡️ Warmup Score</span>
                    <span style={{ color:a.warmupScore>70?'#2ECC71':a.warmupScore>40?'#F39C12':'#E74C3C', fontWeight:700 }}>{a.warmupScore}/100</span>
                  </div>
                  <div style={{ background:'#1a1a1a', borderRadius:4, height:6 }}>
                    <div style={{ width:`${a.warmupScore}%`, height:'100%', borderRadius:4, background:a.warmupScore>70?'#2ECC71':a.warmupScore>40?'#F39C12':'#E74C3C' }} />
                  </div>
                </div>
                <div style={{ display:'flex', gap:8, fontSize:12, color:'#555', marginBottom:14 }}>
                  <span>📤 {a.sentToday}/{a.dailyLimit} today</span><span>·</span><span>Total: {a.totalSent}</span>
                </div>
                <div style={{ display:'flex', gap:8 }}>
                  <Button size="sm" variant={a.warmupEnabled?'secondary':'outline'} onClick={() => toggleWarmup(a.id, a.warmupEnabled)} style={{ flex:1 }}>
                    {a.warmupEnabled ? '⏸ Stop Warmup' : '🌡️ Warmup'}
                  </Button>
                  <Button size="sm" variant="danger" onClick={() => deleteAccount(a.id)}>🗑</Button>
                </div>
              </Card>
            ))}
          </div>
        )
      )}

      {/* Manual SMTP Modal */}
      <Modal open={showModal} onClose={() => setShowModal(false)} title="Manual SMTP Setup" size="md">
        <Alert type="info" style={{ marginBottom:16 }}>Use this for custom SMTP servers. For Gmail/Microsoft, use the OAuth buttons above.</Alert>
        <Input label="Account Name" placeholder="e.g. Sales Account" value={form.name} onChange={e => f('name',e.target.value)} />
        <Input label="Email Address *" type="email" placeholder="you@yourdomain.com" value={form.email} onChange={e => f('email',e.target.value)} />
        <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr', gap:12 }}>
          <Input label="SMTP Host *" placeholder="smtp.yourdomain.com" value={form.smtpHost} onChange={e => f('smtpHost',e.target.value)} />
          <Input label="SMTP Port" type="number" value={form.smtpPort} onChange={e => f('smtpPort',e.target.value)} />
        </div>
        <Input label="SMTP Username" placeholder="you@yourdomain.com" value={form.smtpUser} onChange={e => f('smtpUser',e.target.value)} />
        <Input label="SMTP Password" type="password" placeholder="App password or SMTP password" value={form.smtpPassword} onChange={e => f('smtpPassword',e.target.value)} />
        <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr', gap:12 }}>
          <Input label="IMAP Host" placeholder="imap.yourdomain.com" value={form.imapHost} onChange={e => f('imapHost',e.target.value)} />
          <Input label="IMAP Port" type="number" value={form.imapPort} onChange={e => f('imapPort',e.target.value)} />
        </div>
        <div style={{ display:'flex', gap:10, marginTop:8 }}>
          <Button variant="secondary" onClick={() => setShowModal(false)} style={{ flex:1 }}>Cancel</Button>
          <Button loading={saving} onClick={addSMTP} style={{ flex:2 }}>Connect Account</Button>
        </div>
      </Modal>
    </div>
  );
}
